import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  MarketingPreference,
  MarketingPreferenceSchema,
  MarketingPreferencesResponse,
  MarketingPreferencesSchema,
  UpdateMarketingPreferencesRequest,
  UpdateMarketingPreferencesRequestSchema,
} from '../../models/marketing-preferences/marketing-preferences-api.model';
import { ClientStore } from '../../stores/client/client.store';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class MarketingPreferencesService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly clientStore = inject(ClientStore);

  /**
   * Gets marketing preferences for a specific client
   * @param clientId - The client ID to get marketing preferences for
   * @returns Observable that emits the first marketing preference or null if array is empty
   */
  get(clientId: number): Observable<MarketingPreference | null> {
    return this.http
      .get<MarketingPreferencesResponse>(this.getMarketingPreferencesRetrievalUrl(clientId))
      .pipe(
        this.verifyResponse(MarketingPreferencesSchema),
        map((response: MarketingPreferencesResponse) => (response.length > 0 ? response[0] : null)),
        tap((preference) => {
          if (preference) {
            this.clientStore.setMarketingPreferences(preference);
          }
        }),
        catchError((error: HttpErrorResponse) => throwError(() => this.handleError(error)))
      );
  }

  /**
   * Saves marketing preferences for a specific client
   * @param request - The marketing preferences data to save
   * @returns Observable that emits the saved marketing preference
   */
  save(request: UpdateMarketingPreferencesRequest): Observable<MarketingPreference> {
    // Validate the request before sending
    const validatedRequest = UpdateMarketingPreferencesRequestSchema.parse(request);

    return this.http
      .post<MarketingPreference>(this.getMarketingPreferencesUpdateUrl(), validatedRequest)
      .pipe(
        this.verifyResponse(MarketingPreferenceSchema),
        map((response: MarketingPreference) => response),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => this.handleError(error));
        })
      );
  }

  private handleError(error: HttpErrorResponse): Error {
    let errorMessage = 'An unexpected error occurred. Please try again.';

    switch (error.status) {
      case 0:
        errorMessage = 'Network error. Please check your connection and try again.';
        break;
      default:
        break;
    }

    return new Error(errorMessage);
  }

  private getMarketingPreferencesRetrievalUrl(clientId: number): string {
    return environment.marketingPreferences.replace('{{clientId}}', clientId.toString());
  }

  private getMarketingPreferencesUpdateUrl(): string {
    return environment.marketingPreferences.replace('/{{clientId}}', '');
  }
}
